import { isBirthdateFromMin, isBirthdateOfAge, MIN_BIRTHDATE, maxBirthdate } from 'shared/birthdate'

type DatePart = 'day' | 'month' | 'year'

export type DateLayout = { order: DatePart[]; separator: string }

const PART_LENGTH: Record<DatePart, number> = { day: 2, month: 2, year: 4 }
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

const isDatePart = (type: string): type is DatePart => type in PART_LENGTH

type PartRanges = Record<DatePart, { min: number; max: number }>

/** The values each part may take: any day and month, and the years a birthdate may fall in (shared/birthdate.ts). */
export function birthdateRanges(today?: Date): PartRanges {
  return {
    day: { min: 1, max: 31 },
    month: { min: 1, max: 12 },
    year: { min: Number(MIN_BIRTHDATE.slice(0, 4)), max: Number(maxBirthdate(today).slice(0, 4)) },
  }
}

const inRange = (value: number, range: { min: number; max: number }) => value >= range.min && value <= range.max

/** Whether some full-length value in range starts with `digits` (`3` → 30, 31 as a day; `18` → no year). */
const canGrowInto = (digits: string, length: number, range: { min: number; max: number }) =>
  Number(digits.padEnd(length, '0')) <= range.max && Number(digits.padEnd(length, '9')) >= range.min

const layoutCache = new Map<string, DateLayout>()

/** The locale's numeric date order and separator: day/month/year for es-ES, month/day/year for en-US. */
export function dateLayout(locale: string): DateLayout {
  const cached = layoutCache.get(locale)
  if (cached) return cached
  const parts = new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: 'numeric' }).formatToParts(
    new Date(2000, 11, 31),
  )
  const order = parts.map((part) => part.type).filter(isDatePart)
  const layout: DateLayout =
    order.length === 3
      ? { order, separator: parts.find((part) => part.type === 'literal')?.value ?? '/' }
      : { order: ['day', 'month', 'year'], separator: '/' }
  layoutCache.set(locale, layout)
  return layout
}

/**
 * Keeps digits only, split into the layout's parts. Out-of-range values are typed as is and reported by
 * {@link dateTextProblem}; the mask only pads a day or month digit that can only stand alone (`4` → `04`), and a
 * typed separator closes a short part (`1/5/` → `01/05/`). A full part gets its separator at once, unless the user
 * is deleting (`autoSeparator: false`), or backspacing over it would put it straight back.
 */
export function maskDateText(
  raw: string,
  layout: DateLayout,
  autoSeparator = true,
  ranges: PartRanges = birthdateRanges(),
): string {
  if (ISO_DATE.test(raw.trim())) {
    const [year = '', month = '', day = ''] = raw.trim().split('-')
    return formatParts({ day, month, year }, layout)
  }
  const done: string[] = []
  let current = ''
  for (const char of raw) {
    let part = layout.order[done.length]
    if (!part) break
    if (char >= '0' && char <= '9') {
      if (current.length === PART_LENGTH[part]) {
        if (done.length === layout.order.length - 1) break
        done.push(current)
        current = ''
        part = layout.order[done.length]
        if (!part) break
      }
      const length = PART_LENGTH[part]
      const range = ranges[part]
      current += char
      const onlyStandsAlone =
        part !== 'year' &&
        current.length === 1 &&
        !canGrowInto(current, length, range) &&
        inRange(Number(current), range)
      if (onlyStandsAlone) current = current.padStart(length, '0')
    } else if (current && part !== 'year') {
      done.push(current.padStart(PART_LENGTH[part], '0'))
      current = ''
    }
  }
  const part = layout.order[done.length]
  if (autoSeparator && part && current.length === PART_LENGTH[part] && done.length < layout.order.length - 1) {
    done.push(current)
    current = ''
  }
  const closed = done.length > 0 && done.length < layout.order.length
  return done.join(layout.separator) + (closed ? layout.separator : '') + current
}

export type DateProblem = 'day' | 'month' | 'invalid' | 'before-min' | 'under-age'

/**
 * What is wrong with `text` so far, reported as soon as a part is complete: a day or month out of range, a year
 * before 1900 or too recent for the minimum age, then, once whole, a day that does not exist or a birthdate out of
 * bounds. Undefined while nothing typed is wrong yet.
 */
export function dateTextProblem(
  text: string,
  layout: DateLayout,
  ranges: PartRanges = birthdateRanges(),
  today?: Date,
): DateProblem | undefined {
  const segments = text.split(layout.separator)
  for (const [index, part] of layout.order.entries()) {
    const segment = segments[index] ?? ''
    if (segment.length !== PART_LENGTH[part]) continue
    const value = Number(segment)
    if (part === 'year') {
      if (value < ranges.year.min) return 'before-min'
      if (value > ranges.year.max) return 'under-age'
    } else if (!inRange(value, ranges[part])) return part
  }
  if (layout.order.some((part, index) => (segments[index] ?? '').length !== PART_LENGTH[part])) return
  const iso = parseDateText(text, layout)
  if (!iso) return 'invalid'
  if (!isBirthdateFromMin(iso)) return 'before-min'
  if (!isBirthdateOfAge(iso, today)) return 'under-age'
}

/** Days in the typed month, or 31 while it is unknown; February has 29 until the year says otherwise. */
function daysInMonth(month: string, year: string): number {
  const monthNumber = Number(month)
  if (month.length !== 2 || monthNumber < 1 || monthNumber > 12) return 31
  const yearNumber = year.length === 4 ? Number(year) : 2000
  return new Date(Date.UTC(yearNumber, monthNumber, 0)).getUTCDate()
}

/**
 * The Up/Down arrows on the part under the caret, as a native date input does: day and month wrap around (the day
 * within the typed month's length), the year steps within its range, and an empty part starts at its range's
 * start (the year at the latest one allowed). Returns the new text and the stepped part's span, to select it.
 */
export function stepDateText(
  text: string,
  layout: DateLayout,
  caret: number,
  delta: 1 | -1,
  ranges: PartRanges = birthdateRanges(),
): { text: string; selection: [number, number] } {
  const segments = text.split(layout.separator)
  const index = Math.min(text.slice(0, caret).split(layout.separator).length - 1, layout.order.length - 1)
  const part = layout.order[index] ?? 'day'
  const valueOf = (wanted: DatePart) => segments[layout.order.indexOf(wanted)] ?? ''
  const range = part === 'day' ? { min: 1, max: daysInMonth(valueOf('month'), valueOf('year')) } : ranges[part]
  const current = segments[index] ?? ''
  let next: number
  if (current === '') next = part === 'year' ? range.max : delta > 0 ? range.min : range.max
  else {
    next = Number(current) + delta
    if (part === 'year') next = Math.min(Math.max(next, range.min), range.max)
    else if (next > range.max) next = delta > 0 ? range.min : range.max
    else if (next < range.min) next = range.max
  }
  const stepped = String(next).padStart(PART_LENGTH[part], '0')
  const steppedSegments = layout.order.map((_, at) => (at === index ? stepped : (segments[at] ?? '')))
  const last = Math.max(index, ...steppedSegments.map((segment, at) => (segment ? at : 0)))
  const trailing = last < layout.order.length - 1 ? layout.separator : ''
  const start = steppedSegments.slice(0, index).reduce((length, segment) => length + segment.length, 0)
  const selectionStart = start + index * layout.separator.length
  return {
    text: steppedSegments.slice(0, last + 1).join(layout.separator) + trailing,
    selection: [selectionStart, selectionStart + stepped.length],
  }
}

/**
 * What of the pattern is still to type after `text`, as masked by {@link maskDateText}: the rest of the part being
 * typed, then every later part (`31/1` → `M/YYYY`). Drawn right after the typed text, so the pattern stays in view.
 */
export function dateGhost(text: string, layout: DateLayout, placeholder: Record<DatePart, string>): string {
  const segments = text.split(layout.separator)
  const current = segments.length - 1
  const part = layout.order[current]
  if (!part) return ''
  const rest = layout.order.slice(current + 1).map((later) => layout.separator + placeholder[later])
  return placeholder[part].slice(segments[current]?.length ?? 0) + rest.join('')
}

/**
 * `YYYY-MM-DD` when `text` names a real calendar day, else undefined. The birthdate bounds are the schema's to
 * report (shared/applicant-form.ts), so a day out of them still parses and gets its own message.
 */
export function parseDateText(text: string, layout: DateLayout): string | undefined {
  const segments = text.split(layout.separator)
  if (segments.length !== 3) return
  const value: Partial<Record<DatePart, string>> = {}
  layout.order.forEach((part, index) => (value[part] = segments[index]))
  const { day = '', month = '', year = '' } = value
  if (year.length !== 4 || !day || !month) return
  const iso = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
  const date = new Date(`${iso}T00:00:00Z`)
  if (Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== iso) return
  return iso
}

export function formatIsoDate(iso: string, layout: DateLayout): string {
  const [year = '', month = '', day = ''] = iso.split('-')
  return formatParts({ day, month, year }, layout)
}

function formatParts(value: Record<DatePart, string>, layout: DateLayout): string {
  return layout.order.map((part) => value[part]).join(layout.separator)
}
